import utilsGraficos from "../../../utils/utilsGraficos";
import tiposFinanciamento from "../../../constants/tiposFinanciamento";

// Mesma lista/lógica de Relatorio/TabelaRelatorio/index.js:totalFaculdade() —
// Faculdade é a soma dessas 3 modalidades, EP é o restante (total - Faculdade),
// não duas listas separadas.
const MODALIDADES_FACULDADE = [
    "Cursos de Extensão Presencial",
    'Pós-Graduação "lato sensu" - Especializa',
    "Graduação Tecnológica Presencial",
];

function somaMetrica(linhas, campo, mes) {
    let filtradas = mes !== undefined ? utilsGraficos.filtrar(linhas, { mes }) : linhas;
    return filtradas.reduce((acc, linha) => acc + (linha[campo] || 0), 0);
}

function somaPorGrupo(linhas, propriedade, campo, mes) {
    let grupos = utilsGraficos.groupBy(linhas, propriedade);
    let resultado = {};
    Object.keys(grupos).forEach((chave) => {
        resultado[chave] = somaMetrica(grupos[chave], campo, mes);
    });
    return resultado;
}

function somaPorGrupoEMes(linhas, propriedade, campo) {
    let grupos = utilsGraficos.groupBy(linhas, propriedade);
    let resultado = {};
    Object.keys(grupos).forEach((chave) => {
        let porMes = utilsGraficos.groupBy(grupos[chave], "mes");
        resultado[chave] = {};
        Object.keys(porMes).forEach((mes) => {
            resultado[chave][mes] = somaMetrica(porMes[mes], campo);
        });
    });
    return resultado;
}

// Substitui o widget "EP vs. Faculdade" (decisão do usuário, 2026-08-28):
// abre a métrica selecionada pelos 3 tipos de financiamento (já centralizados
// no item 8, `constants/tiposFinanciamento.js`) + Total, sem separar por
// EP/Faculdade -- essa comparação continua existindo em `resumoUniplan`
// (mais abaixo na tela), mas só pras 2 métricas fixas dele; aqui responde à
// métrica escolhida no filtro.
function somaPorTipoFinanciamento(linhas, campo, mes) {
    let porTipo = {};
    tiposFinanciamento.TIPOS.forEach(({ slug, label }) => {
        porTipo[slug] = somaMetrica(linhas.filter((linha) => linha.tipo === label), campo, mes);
    });
    porTipo.total = somaMetrica(linhas, campo, mes);
    return porTipo;
}

function paraSerieTreemap(mapaSomas) {
    return Object.keys(mapaSomas).map((chave) => ({ x: chave, y: mapaSomas[chave] }));
}

// Volta pra granularidade de curso individual (mesma tabela de Relatorio/Cursos):
// as linhas do cubo são uma por curso×mês, agrupadas de volta por `id` (o _id do
// meta, resolvido de verdade nesse endpoint — ver Fase 1). "Turmas" soma numTurmas
// respeitando o mes selecionado, mesma lógica de Relatorio/Cursos/index.js:totalTurmas().
function detalheCursos(linhas, mes) {
    let grupos = utilsGraficos.groupBy(linhas, "id");
    return Object.keys(grupos).map((id) => {
        let rowsDoCurso = grupos[id];
        let base = rowsDoCurso[0];
        return {
            id,
            curso: base.curso,
            unidade: base.unidade,
            CR: base.CR,
            modalidade: base.modalidade,
            tipo: base.tipo,
            turno: base.turno,
            cargaHoraria: base.cargaHoraria,
            comentario: base.comentario,
            turmas: somaMetrica(rowsDoCurso, "numTurmas", mes),
        };
    });
}

// Subconjunto das colunas de Relatorio/TabelaRelatorio/Tabela/index.js (hoje
// lidas por índice de relatorioUtils.js:loadTotalModalidade — total[0]..total[9]),
// aqui por nome de campo. Carga H. Turma/Ano e Necessidade de docente (8h/7,2h)
// ficam de fora a pedido do usuário. Linha extra "TOTAL" no fim, mesmo padrão da tela antiga.
const COLUNAS_RESUMO_MODALIDADE = [
    "numTurmas",
    "numAlunosTurmas",
    "cargaHoraria",
    "numMatriculasResiduais",
    "numMatriculasNovas",
    "numTotalMatriculas",
    "horaAlunoDistribuidaPercentualEvasao",
];

function linhaResumo(chavePropriedade, chave, linhasDoGrupo, mes) {
    let linha = { [chavePropriedade]: chave };
    COLUNAS_RESUMO_MODALIDADE.forEach((campo) => {
        linha[campo] = somaMetrica(linhasDoGrupo, campo, mes);
    });
    return linha;
}

function resumoPorModalidade(linhas, mes) {
    let grupos = utilsGraficos.groupBy(linhas, "modalidade");
    let porModalidade = Object.keys(grupos).map((modalidade) => linhaResumo("modalidade", modalidade, grupos[modalidade], mes));
    let total = linhaResumo("modalidade", "TOTAL", linhas, mes);
    return [...porModalidade, total];
}

// Nível 2 do drill-down do ResumoModalidades: cursos dentro de uma modalidade
// (já filtrada pelo chamador). Agrupa por `id` (o _id real do meta) em vez de
// `modalidade` — mesmas colunas de métrica. `unidade` entra no rótulo porque,
// com o filtro global de unidade em "Todas", uma modalidade pode ter cursos de
// mais de uma unidade; sem isso, dois cursos homônimos ficariam ambíguos.
function resumoPorCurso(linhas, mes) {
    let grupos = utilsGraficos.groupBy(linhas, "id");
    let porCurso = Object.keys(grupos).map((id) => {
        let base = grupos[id][0];
        let linha = linhaResumo("id", id, grupos[id], mes);
        linha.curso = base.curso;
        linha.unidade = base.unidade;
        linha.comentario = base.comentario;
        return linha;
    });
    let total = linhaResumo("id", "TOTAL", linhas, mes);
    total.curso = "TOTAL";
    return [...porCurso, total];
}

const MESES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

// Nível 3 do drill-down: detalhamento mensal de um curso. Cada linha do cubo já
// é um mês daquele curso (sem soma) — ignora `mes` por design, sempre mostra
// todos os meses que existirem, na ordem do calendário (mesmo comportamento do
// Relatorio/TabelaRelatorio/DetalheCurso antigo, que também ignorava o filtro
// de mês do pai).
function detalheMensalCurso(linhas, id) {
    let doCurso = linhas.filter((linha) => linha.id === id);
    return MESES.map((mes) => doCurso.find((linha) => linha.mes === mes)).filter(Boolean);
}

// "Resumo UNIPLAN": mesmo cruzamento de Relatorio/Exportacao/Tabela2/index.js —
// matrículas e hora/aluno (% evasão) por modalidade, abertos nas 3 colunas de
// tipo de financiamento (`constants/tiposFinanciamento.js`, já centralizado no
// item 8) + coluna Total, separados em bloco Educação Profissional / Faculdade.
// Aqui por nome de campo (a tela antiga lia por índice de array — total[5]/[6] —
// mesma classe de fragilidade já evitada no resto deste dashboard) e usando a
// MESMA lista de MODALIDADES_FACULDADE já usada no EpVsFaculdade/Treemap deste
// dashboard (a Tabela2 antiga usa uma lista de 4 modalidades, incluindo "Cursos
// de Extensão a Distância" — decisão do usuário: unificar numa lista só, pra não
// ter dois números de "Faculdade" divergentes no mesmo dashboard).
function linhaUniplan(modalidade, linhasDoGrupo, mes) {
    let linha = { modalidade };
    let matriculaTotal = 0;
    let horaTotal = 0;
    tiposFinanciamento.TIPOS.forEach(({ slug, label }) => {
        let linhasDoTipo = linhasDoGrupo.filter((l) => l.tipo === label);
        let matricula = somaMetrica(linhasDoTipo, "numTotalMatriculas", mes);
        let hora = somaMetrica(linhasDoTipo, "horaAlunoDistribuidaPercentualEvasao", mes);
        linha[`matricula_${slug}`] = matricula;
        linha[`hora_${slug}`] = hora;
        matriculaTotal += matricula;
        horaTotal += hora;
    });
    linha.matriculaTotal = matriculaTotal;
    linha.horaTotal = horaTotal;
    return linha;
}

function resumoUniplan(linhas, mes) {
    let ehFaculdade = (modalidade) => MODALIDADES_FACULDADE.includes(modalidade);
    let grupos = utilsGraficos.groupBy(linhas, "modalidade");

    let linhasEP = Object.keys(grupos).filter((m) => !ehFaculdade(m)).map((m) => linhaUniplan(m, grupos[m], mes));
    let linhasFaculdade = Object.keys(grupos).filter(ehFaculdade).map((m) => linhaUniplan(m, grupos[m], mes));

    let totalEP = linhaUniplan("Total Educação Profissional", linhas.filter((l) => !ehFaculdade(l.modalidade)), mes);
    let totalFaculdade = linhaUniplan("Total Faculdade", linhas.filter((l) => ehFaculdade(l.modalidade)), mes);
    let totalGeral = linhaUniplan("TOTAL", linhas, mes);

    return { linhasEP, totalEP, linhasFaculdade, totalFaculdade, totalGeral };
}

export default {
    MODALIDADES_FACULDADE,
    COLUNAS_RESUMO_MODALIDADE,
    somaMetrica,
    somaPorGrupo,
    somaPorGrupoEMes,
    somaPorTipoFinanciamento,
    paraSerieTreemap,
    detalheCursos,
    resumoPorModalidade,
    resumoPorCurso,
    detalheMensalCurso,
    resumoUniplan,
};
